<template>
  <div class="primaryLight--text mx-5 mt-3">
    <v-toolbar elevation="2" class="my-3">
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <v-toolbar-title class="primaryLight--text">My Bookings</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
      </template>
      <v-spacer></v-spacer>
      <v-dialog v-model="newBookingDialog" max-width="500px">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primaryLight" depressed class="white--text" v-bind="attrs" v-on="on" @click="openNewBookingDialog()">
            <v-icon class="mr-2">mdi-plus</v-icon>Add new Booking
          </v-btn>
        </template>

        <v-card>
          <v-card-title class="primaryLight--text">Booking basic details</v-card-title>
          <v-card-text class="pb-0">
            <v-container>
              <!-- START FORM -->
              <v-form v-model="newBookingFormValid">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-checkbox
                      class="mt-0"
                      v-model="newBooking.is_self_booking"
                      label="Is this person self booking?">
                    </v-checkbox>
                  </v-col>

                  <v-col cols="12" class="py-0">
                    <v-checkbox
                      v-model="newBooking.is_house_call"
                      class="mt-0"
                      label="Is this person booking a house call?">
                    </v-checkbox>
                  </v-col>

                  <!-- START BOOKER FIELD -->
                  <v-col v-if="!newBooking.is_self_booking" cols="12" class="py-0">
                    <v-text-field
                      outlined dense color="primaryLight"
                      type="text" v-model="newBooking.booker" label="Enter name of representative">
                    </v-text-field>
                  </v-col>

                  <!-- START RELATIONSHIP FIELD -->
                  <v-col v-if="!newBooking.is_self_booking" cols="12" class="py-0">
                    <v-text-field
                      outlined dense color="primaryLight"
                      type="text" v-model="newBooking.relationship_with_booker" label="Client relationship with representative">
                    </v-text-field>
                  </v-col>

                  <!-- START CLIENT SELECT FIELD -->
                  <v-col cols="12" class="py-0">
                    <v-autocomplete
                      :items="clients"
                      hint="Can't find your client? Make sure to add them first in the clients menu!"
                      persistent-hint
                      outlined dense
                      color="primaryLight"
                      item-text="name"
                      item-value="id"
                      label="Choose a client"
                      v-model="newBooking.client_id"
                      :error-messages="newBookingErrors.client[0]"
                      @focus="newBookingErrors.client = []"
                      required>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-form>
              <!-- END FORM -->

              <p v-if="newBookingErrors.contact_details" class="red--text">{{newBookingErrors.contact_details[0]}}</p>
            </v-container>
          </v-card-text>
          <v-card-actions class="pb-5 pr-8 pt-0">
            <v-spacer></v-spacer>
            <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
            <v-btn depressed color="primaryLight" class="white--text"
              :loading="createBookingLoading" :disabled="createBookingLoading || !newBooking.client_id" @click="addBooking()">
              Add booking
              <template v-slot:loader>
                <span>Processing...</span>
              </template>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="bookings"
      item-key="id"
      :items-per-page="5"
      :search="search"
      class="mb-5 primaryLight--text elevation-3">

      <!-- Search field -->
      <template v-slot:top>
        <v-text-field
          outlined dense
          v-model="search"
          label="Search by client name"
          append-icon="mdi-magnify"
          class="mx-4 py-3"
          hide-details
        ></v-text-field>
      </template>

      <!-- Column field -->
      <template v-slot:item.client_name="{ item }">
        <p class="admin__row-field">{{item.client_name}}</p>
      </template>
      <template v-slot:item.details="{ item }">
        <p class="admin__row-field">{{constructDetails(item.details)}}</p>
      </template>

      <!-- Action field -->
      <template v-slot:item.actions="{ item }">
        <v-icon class="mr-2 green--text" @click="openEditBookingDialog(item)">mdi-pencil</v-icon>
        <v-icon class="red--text" @click="openDeleteBookingDialog(item)">mdi-delete</v-icon>
      </template>

      <!-- Refetch button -->
      <template v-slot:no-data>
        <v-btn color="primaryLight my-10 white--text" @click="getBookings()">Refetch booking list</v-btn>
      </template>
    </v-data-table>

    <!-- Detail dialog -->
    <v-dialog v-model="editBookingDialog" max-width="500px">
      <v-card>
        <v-card-title class="primaryLight--text">Client basic details</v-card-title>
        <v-card-text class="pb-0">
          <v-container>
            <!-- START FORM -->
            <v-form v-model="editBookingFormValid">
              <v-row>
                <v-col cols="12" class="py-0">
                  <v-checkbox
                    class="mt-0"
                    v-model="editedBooking.is_self_booking"
                    label="Is this person self booking?">
                  </v-checkbox>
                </v-col>

                <v-col cols="12" class="py-0">
                  <v-checkbox
                    v-model="editedBooking.is_house_call"
                    class="mt-0"
                    label="Is this person booking a house call?">
                  </v-checkbox>
                </v-col>

                <v-col v-if="editedBooking.is_house_call" cols="12" class="py-0">
                  <p class="mb-10 primaryLight--text">{{editedBooking.address ? `Her address is at: ${editedBooking.address}` : 'There is no address set for this client yet. Please do so in the clients menu.'}}</p>
                </v-col>

                <!-- START BOOKER FIELD -->
                <v-col v-if="!editedBooking.is_self_booking" cols="12" class="py-0">
                  <v-text-field
                    outlined dense color="primaryLight"
                    type="text" v-model="editedBooking.booker" label="Enter name of representative">
                  </v-text-field>
                </v-col>

                <!-- START RELATIONSHIP FIELD -->
                <v-col v-if="!editedBooking.is_self_booking" cols="12" class="py-0">
                  <v-text-field
                    outlined dense color="primaryLight"
                    type="text" v-model="editedBooking.relationship_with_booker" label="Client relationship with representative">
                  </v-text-field>
                </v-col>

                <!-- START CLIENT SELECT FIELD -->
                <v-col cols="12" class="py-0">
                  <v-autocomplete
                    :items="clients"
                    hint="Can't find your client? Make sure to add them first in the clients menu!"
                    persistent-hint
                    outlined dense
                    color="primaryLight"
                    item-text="name"
                    item-value="id"
                    label="Choose a client"
                    v-model="editedBooking.client_id"
                    :error-messages="newBookingErrors.client[0]"
                    @focus="newBookingErrors.client = []"
                    required>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </v-form>
            <!-- END FORM -->

          </v-container>
        </v-card-text>

        <v-card-actions class="pb-5 pr-8 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
          <v-btn depressed color="primaryLight" class="white--text"
            :loading="editBookingLoading" :disabled="editBookingLoading || !editedBooking.client_id" @click="updateBooking()">
            Save changes
            <template v-slot:loader>
              <span>Processing...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete dialog -->
    <v-dialog v-model="deleteBookingDialog" max-width="500px">
      <v-card>
        <v-card-title class="primaryLight--text">Are you sure you want to remove<br>{{deletedBooking.client_name}} booking from your list?</v-card-title>
        <v-card-actions class="pb-5 pr-8">
          <v-spacer></v-spacer>
          <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
          <v-btn color="primaryLight white--text" @click="deleteBooking()">Yes, remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import bookingsCollection from '../services/firestore/booking'
  import clientsCollection from '../services/firestore/client'

  export default {
    name: 'BookingsList',
    data() {
      return {
        search: '',
        newBookingDialog: false,
        editBookingDialog: false,
        deleteBookingDialog: false,
        headers: [
          { text: 'Client', value: 'client_name' },
          { text: 'Details', value: 'details'},
          { text: 'Admin actions', value: 'actions', sortable: false }
        ],
        bookings: [],
        clients: [],
        newBooking: {
          is_self_booking: false,
          is_house_call: false
        },
        editedBooking: {
          is_self_booking: false,
          is_house_call: false
        },
        deletedBooking: {},

        // Form
        newBookingFormValid: false,
        editBookingFormValid: false,
        createBookingLoading: false,
        editBookingLoading: false,
        newBookingErrors: { client: [] },
        editBookingErrors: { client: [] }
      }
    },
    watch: {
      editBookingDialog (val) {
        val || this.close()
      },
      deleteBookingDialog (val) {
        val || this.close()
      },
    },
    created() {
      this.getBookings()
      this.getClients()
    },
    methods: {
      constructDetails(details) {
        const message = `${details.booker} is booking for their ${details.relationship_with_booker}.`
        return `
          ${details.is_self_booking ? 'Client is self booking' : message}
        `
      },
      openNewBookingDialog() {
        this.newBooking = {}
        this.createBookingLoading = false
      },
      getBookings() {
        this.bookings = bookingsCollection.index()
      },
      getClients() {
        this.clients = clientsCollection.index()
      },
      addBooking() {
        this.newBookingErrors = { client: [] }
        this.createBookingLoading = true
        bookingsCollection.create({
          client_id: this.newBooking.client_id,
          details: {
            is_self_booking: this.newBooking.is_self_booking || false,
            booker: this.newBooking.booker || 'Self',
            relationship_with_booker: this.newBooking.relationship_with_booker || 'self',
            is_house_call: this.newBooking.is_house_call || false
          }
        }).then(() => {
          this.getBookings()
          this.createBookingLoading = false
          this.close()
        }).catch(err => {
          this.createBookingLoading = false
          this.newBookingErrors = { name: err.response.data.name || [] }
        })
      },
      openEditBookingDialog(booking) {
        this.editedBooking = {
          id: booking.id,
          client_id: booking.client_id,
          is_self_booking: booking.details.is_self_booking,
          booker: booking.details.booker,
          relationship_with_booker: booking.details.relationship_with_booker,
          is_house_call: booking.details.is_house_call,
          address: booking.address
        }
        this.editBookingDialog = true
      },
      updateBooking() {
        this.editBookingErrors = { client: [] }
        this.editBookingLoading = true
        bookingsCollection.update({
          id: this.editedBooking.id,
          client_id: this.editedBooking.client_id,
          details: {
            is_self_booking: this.editedBooking.is_self_booking || false,
            booker: this.editedBooking.is_self_booking ? '' : this.editedBooking.booker,
            relationship_with_booker: this.editedBooking.is_self_booking ? 'self' : this.editedBooking.relationship_with_booker,
            is_house_call: this.editedBooking.is_house_call || false
          }
        }).then(() => {
          this.getBookings()
          this.editBookingLoading = false
          this.close()
        }).catch(err => {
          this.editBookingLoading = false
          this.editBookingErrors = { name: err.response.data.client || [] }
        })
      },
      openDeleteBookingDialog(booking) {
        this.deletedBooking = booking
        this.deleteBookingDialog = true
      },
      deleteBooking() {
        bookingsCollection.delete(this.deletedBooking.id).then(() => {
          this.getBookings()
          this.close()
        })
      },
      close() {
        this.newBookingDialog = false
        this.editBookingDialog = false
        this.deleteBookingDialog = false
        this.newBooking = {}
        this.editedBooking = {}
        this.deletedBooking = {}
      }
    }
  }
</script>
